import { cn } from '@ui/lib/utils'
import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

const buttonVariants = cva(
  cn(
    'inline-flex items-center justify-center whitespace-nowrap text-sm font-semibold ring-offset-surface-primary',
    'transition-colors duration-300 ease-in-out focus-visible:outline-none focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:text-content-disabled disabled:bg-surface-disabled disabled:border-0',
  ),
  {
    variants: {
      variant: {
        primary:
          'bg-surface-brand-primary text-content-primary-on-brand hover:bg-surface-brand-hover',
        secondary:
          'bg-surface-secondary text-content-secondary hover:bg-surface-secondary-hover',
        secondary_danger:
          'bg-surface-error-tertiary text-content-error-primary border-none hover:bg-surface-error-secondary rounded-m',
        tertiary:
          'text-content-brand-primary hover:text-content-brand-primary-hover disabled:bg-transparent',

        red_primary:
          'bg-surface-error-primary text-content-primary-on-brand hover:bg-surface-error-primary-hover',
        red_secondary:
          'bg-surface-primary text-content-error-primary border-stroke-error-primary border hover:bg-surface-error-secondary',
        red_tertiary:
          'text-content-error-primary hover:text-content-error-primary-hover disabled:bg-transparent',

        none: '',
      },
      size: {
        l: 'min-w-[80px] h-l text-base gap-xxs px-s rounded-lg',
        m: 'min-w-[80px] h-m text-base gap-xxs px-xs rounded-m',
        s: 'min-w-[80px] h-s text-sm gap-xxxs px-xxs rounded-sm',

        icon_l: 'size-l',
        icon_m: 'size-m',
        icon_s: 'size-s',
        icon_xs: 'size-xs',

        none: '',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'm',
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  as?: string
  dataTestId?: string
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      as = 'button',
      dataTestId,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : as

    return (
      <Comp
        data-testid={dataTestId}
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
